<template>
  <div class="nps-score">
    <!-- Header Begins -->
    <div class="column is-full" v-if="internalAttributes.header.show">
      <span class="header-section">
        <div class="columns divider is-align-items-center is-flex-mobile">
          <div class="column has-text-left bold-text">
            {{internalAttributes.header.title}}
          </div>
          <div class="column has-text-right bold-nps-text">
            <animated-number
              round="1"
              :duration="500"
              :formatValue="formatStats"
              :value="nps.score"
            />
          </div>
        </div>
      </span>
    </div> <!-- Header Ends -->
    <!-- Main section Begins -->
    <div class="columns is-flex-mobile has-text-centered">
      <div class="column">
        <img class="breakdown-box__smiley" src="/static/img/reviews/detractors.svg">
        <div
          class="breakdown-box__bar breakdown-box__bar--red"
          :style="{'--barHeight':`${barHeightCalculator('detractors')}px`}"
        >
        </div>
        <div class="info-box">
          <div class="has-text-weight-bold font-size-18">
            <animated-number
              round="1"
              :duration="500"
              :formatValue="formatDigit"
              :value="nps.detractors"
            />
          </div>
          <span class="font-size-11">Detractors <div>(0 - 6)</div></span>
        </div>
      </div>
      <div class="column">
        <img class="breakdown-box__smiley" src="/static/img/reviews/passives.svg">
        <div
          class="breakdown-box__bar breakdown-box__bar--orange"
          :style="{'--barHeight':`${barHeightCalculator('passives')}px`}"
        >
        </div>
        <div class="info-box">
          <div class="has-text-weight-bold font-size-18">
            <animated-number
              round="1"
              :duration="500"
              :formatValue="formatDigit"
              :value="nps.passives"
            />
          </div>
          <span class="font-size-11">Passives <div>(7 - 8)</div></span>
        </div>
      </div>
      <div class="column">
        <img class="breakdown-box__smiley" src="/static/img/reviews/promoters.svg">
        <div
          class="breakdown-box__bar breakdown-box__bar--green"
          :style="{'--barHeight':`${barHeightCalculator('promoters')}px`}"
        >
        </div>
        <div class="info-box">
          <div class="has-text-weight-bold font-size-18">
            <animated-number
              round="1"
              :duration="500"
              :formatValue="formatDigit"
              :value="nps.promoters"
            />
          </div>
          <span class="font-size-11">Promoters <div>(9-10)</div></span>
        </div>
      </div>
    </div>
  </div> <!-- Main section Ends -->
</template>

<script>
  /* eslint-disable indent,no-new,no-undef,valid-jsdoc,one-var */
  import AnimatedNumber from 'animated-number-vue'

  export default {
    name: 'NpsScoreWidget',
    components: {
      AnimatedNumber
    },
    props: {
      nps: {
        type: Object,
        required: true
      },
      attributes: {
        type: Object,
        default: () => ({})
      }
    },
    data () {
      return {
        availableOptions: ['detractors', 'passives', 'promoters'],
        barHeight: {
          set: [10, 18, 26],
          passives: 0,
          promoters: 0,
          detractors: 0
        }
      }
    },
    computed: {
      /**
       * Compute internal attributes
       * @return {Object}
       */
      internalAttributes () {
        return Object.assign({
          header: {
            show: true,
            title: 'NPS score'
          }
        }, this.attributes)
      }
    },
    methods: {
      /**
       * Calculate the bar height
       * @param {Number} targetVal Value of stat
       * @param {Array} otherVal Value of stat
       * @return {Number}
       */
      calcHeight (targetVal, otherVal) {
        if (otherVal.includes(targetVal)) {
          return this.barHeight.set[1]
        } else {
          otherVal.push(targetVal)
          otherVal.sort((a, b) => a - b)
          return this.barHeight.set[otherVal.indexOf(targetVal)]
        }
      },
      /**
       * Calculate the bar height with various available options
       * @param {String} type Value of stat
       * @return {Number}
       */
      barHeightCalculator (type) {
        let remainingArr = []
        this.availableOptions.forEach((val) => {
          if (val !== type) {
            remainingArr.push(this.nps[val])
          }
        })
        return this.calcHeight(this.nps[type], remainingArr)
      },
      /**
       * Format the float numbers upto two digits after decimal
       * @param {string} value Value of stat
       * @return {string|integer}
       */
      formatStats (value) {
        return !Number.isInteger(value) ? `${Number(value).toFixed(2)}` : value
      },
      /**
       * Format the integer by separator (comma (,)) after three digits (e.g 1,530)
       * @param val
       * @return {string}
       */
      formatDigit (val) {
        return val.toLocaleString()
      }
    }
  }
</script>

<style lang="scss" scoped>
  $text-color: #58595b;

  .nps-score {
    border: solid 1px rgba(88, 89, 91, 0.32);
    padding: 10px 15px;

    .column {
      margin: 0;
      padding: 0;
    }
    .columns.has-text-centered {
      padding: 0 0 15px 0;
    }
    .info-box {
      line-height: 1.2;
    }
  }
  .header-section {
    .divider {
      margin-bottom: 15px !important;
      padding: 0 0 8px 0 !important;
    }
    .bold-text {
      font-size: 12px;
      font-weight: bold;
    }
    .bold-nps-text {
      font-size: 18px;
      font-weight: bold;
    }
  }
  .breakdown-box__smiley {
    width: 18.8px;
    height: 18.8px;
    object-fit: contain;
  }
  .breakdown-box__bar {
    height: 26px;
    margin: 5px 0 12px;
    transition: height 0.8s ease;
  }
  .breakdown-box__bar--red {
    background: linear-gradient(0deg, #ef5546 var(--barHeight), #fff 0, #fff 100%);
  }

  .breakdown-box__bar--orange {
    background: linear-gradient(0deg, #fbbb4f var(--barHeight), #fff 0, #fff 100%);
  }

  .breakdown-box__bar--green {
    background: linear-gradient(0deg, #24b889 var(--barHeight), #fff 0, #fff 100%);
  }
  /* Helper css begins */
  .is-align-items-center {
    align-items: center;
  }
  .font-size {
    &-11 {
      font-size: 11px;
    }
    &-18 {
      font-size: 18px;
    }
  }
  /* Helper css ends */
</style>



// WEBPACK FOOTER //
// src/widgets/home/NpsScoreWidget.vue